import { createSlice, PayloadAction, UnknownAction } from '@reduxjs/toolkit';

import {
  SegmentLocation,
  SegmentStatus,
  TAutoSavingSegmentData,
  TEditorHistoryEntry,
  TEditorHistoryKind,
  TEditorStructuralHistoryChange,
  TEditorStructuralHistoryKind,
  TExtendedSegment,
  THistorySegment,
  TSegmentLocation,
  TServerSegment,
} from '@/shared/types/Editor';

import { getIntersectingTimestamps } from './utils/getIntersectingTimestamps';

type TNewSegmentData = {
  location: TSegmentLocation;
  parentSegmentId: TServerSegment['id'];
  segment: TExtendedSegment;
};

type TSegmentDataToSave = {
  [segmentId: TExtendedSegment['id']]: TAutoSavingSegmentData;
};

type TTextEditHistorySession = {
  before: TServerSegment['dst'] | TServerSegment['src'];
  field: 'dst' | 'src';
  historyId: string;
  segmentId: TServerSegment['id'];
};

type TActiveEditorHistoryTravel = {
  direction: 'redo' | 'undo';
  entry: TEditorHistoryEntry;
  reservationId: string;
};

type TEditorStore = {
  activeHistoryAction: {
    changes: Record<string, Omit<TEditorStructuralHistoryChange, 'after' | 'afterIndex'>>;
    kind: TEditorStructuralHistoryKind;
    operationId: string;
  } | null;
  // Actual data from server
  segmentsFromServerMap: {
    [segmentId: TServerSegment['id']]: TServerSegment;
  };
  // Changeable data
  segmentsMap: {
    [segmentId: TExtendedSegment['id']]: TExtendedSegment;
  };
  activeEditorOperationId: null | string;
  activeHistoryTravel: null | TActiveEditorHistoryTravel;
  activeTextEditHistory: null | TTextEditHistorySession;
  focusedSegmentId: null | TServerSegment['id'];
  historyApplying: boolean;
  historyRedoStack: TEditorHistoryEntry[];
  historyUndoStack: TEditorHistoryEntry[];
  isAutoSaving: boolean;
  isVoiceSuggesting: boolean;
  newSegmentsData: TNewSegmentData[];
  pendingSourceTextEditHistories: Record<TServerSegment['id'], TTextEditHistorySession>;
  // The storage for auto-saving data
  segmentsDataToSave: TSegmentDataToSave;
  segmentsIds: TExtendedSegment['id'][];
  selectedSegmentId: null | TServerSegment['id'];
  // Bumped whenever the whole transcript is replaced (rollback, project load). A pages-layer side
  // effect watches it to drop now-stale per-segment previews (whose segment ids no longer exist).
  transcriptReplacedEpoch: number;
  transcriptVersionDirtyRevision: number;
  transcriptVersionPersistedRevision: number;
};

const createInitialState = (transcriptReplacedEpoch = 0): TEditorStore => ({
  activeEditorOperationId: null,
  activeHistoryAction: null,
  activeHistoryTravel: null,
  activeTextEditHistory: null,
  focusedSegmentId: null,
  historyApplying: false,
  historyRedoStack: [],
  historyUndoStack: [],
  isAutoSaving: false,
  isVoiceSuggesting: false,
  newSegmentsData: [],
  pendingSourceTextEditHistories: {},
  segmentsDataToSave: {},
  segmentsFromServerMap: {},
  segmentsIds: [],
  segmentsMap: {},
  selectedSegmentId: null,
  transcriptReplacedEpoch,
  transcriptVersionDirtyRevision: 0,
  transcriptVersionPersistedRevision: 0,
});

const initialState = createInitialState();

const isHistoryTravelReserved = (state: TEditorStore) => Boolean(state.activeHistoryTravel);

const normalizeSegment = (segment: TServerSegment): TServerSegment => {
  return {
    ...segment,
    dst: segment.dst || { lang: '', text: '' },
    src: segment.src || { lang: '', text: '' },
  };
};

const HISTORY_LIMIT = 100;

const cloneText = (text: TServerSegment['src']) => ({
  ...text,
  words: text.words?.map((word) => ({ ...word })),
});

const snapshotSegment = (segment: TExtendedSegment): THistorySegment => ({
  id: segment.id,
  dst: cloneText(segment.dst),
  end: segment.end,
  historyId: segment.historyId || segment.id,
  needs_attention: Boolean(segment.needs_attention),
  processing_started_at: segment.processing_started_at,
  processing_step: segment.processing_step,
  selected_translation_variant: segment.selected_translation_variant
    ? { ...segment.selected_translation_variant }
    : undefined,
  speaker: segment.speaker,
  src: cloneText(segment.src),
  start: segment.start,
  status: segment.status,
  translation_variants: segment.translation_variants?.map((variant) => ({ ...variant })),
});

const trackSegmentBeforeMutation = (state: TEditorStore, segmentId: string) => {
  const action = state.activeHistoryAction;
  const segment = state.segmentsMap[segmentId];

  if (!action || !segment) {
    return;
  }

  const historyId = segment.historyId || segment.id;

  if (!action.changes[historyId]) {
    action.changes[historyId] = {
      before: snapshotSegment(segment),
      beforeIndex: state.segmentsIds.indexOf(segmentId),
      historyId,
    };
  }
};

const trackInsertedSegment = (state: TEditorStore, segmentId: string) => {
  const action = state.activeHistoryAction;
  const segment = state.segmentsMap[segmentId];

  if (!action || !segment) {
    return;
  }

  const historyId = segment.historyId || segment.id;
  action.changes[historyId] ||= { historyId };
};

const pushHistoryEntry = (state: TEditorStore, entry: TEditorHistoryEntry) => {
  if ('changes' in entry ? entry.changes.length === 0 : JSON.stringify(entry.before) === JSON.stringify(entry.after)) {
    return;
  }

  state.historyUndoStack.push(entry);
  state.historyUndoStack = state.historyUndoStack.slice(-HISTORY_LIMIT);
  state.historyRedoStack = [];
};

const closeActiveTextEdit = (state: TEditorStore) => {
  const activeTextEdit = state.activeTextEditHistory;

  if (!activeTextEdit) {
    return;
  }

  const segment = state.segmentsMap[activeTextEdit.segmentId];

  // Source edits are explicitly confirmed by Translate. Focus handoff and structural actions park
  // their original before-snapshot without making the unconfirmed draft undoable.
  if (segment && activeTextEdit.field === 'src' && segment.isEditingSrc) {
    state.pendingSourceTextEditHistories[activeTextEdit.segmentId] ||= activeTextEdit;
  } else if (segment) {
    pushHistoryEntry(state, {
      after: cloneText(segment[activeTextEdit.field]),
      before: activeTextEdit.before,
      field: activeTextEdit.field,
      historyId: activeTextEdit.historyId,
      kind: 'text',
    });
  }

  state.activeTextEditHistory = null;
};

const recalculateSegmentOverlaps = (state: TEditorStore) => {
  state.segmentsIds.forEach((id) => {
    delete state.segmentsMap[id].overlappedTimestamps;
  });

  const segmentsIdsToCheck = [...state.segmentsIds];

  state.segmentsIds.forEach((id) => {
    const overlappedTimestamps = getIntersectingTimestamps({
      segment: state.segmentsMap[id],
      segmentsIdsToCheck,
      segmentsMap: state.segmentsMap,
    });

    segmentsIdsToCheck.shift();

    Object.keys(overlappedTimestamps).forEach((segmentId) => {
      const segment = state.segmentsMap[segmentId];
      const isAlreadyExist = Boolean(segment?.overlappedTimestamps?.length);

      if (isAlreadyExist) {
        segment.overlappedTimestamps = Array.from(
          new Set([...(segment.overlappedTimestamps || []), ...overlappedTimestamps[segmentId]]),
        );
      } else if (segment) {
        segment.overlappedTimestamps = overlappedTimestamps[segmentId];
      }
    });
  });
};

const editorSlice = createSlice({
  name: 'editor',
  initialState,
  reducers: {
    applyHistoryFlushResponse: (state, { payload }: PayloadAction<TServerSegment[]>) => {
      if (!state.historyApplying) return;

      payload.forEach((segment) => {
        const normalized = normalizeSegment(segment);
        const current = state.segmentsMap[normalized.id];
        state.segmentsFromServerMap[normalized.id] = normalized;

        if (current) {
          state.segmentsMap[normalized.id] = {
            ...current,
            ...normalized,
            historyId: current.historyId || current.id,
          };
        }
      });
    },

    beginTextEditHistory: (
      state,
      { payload }: PayloadAction<{ field: 'dst' | 'src'; segmentId: TServerSegment['id'] }>,
    ) => {
      if (
        !state.activeEditorOperationId &&
        !state.historyApplying &&
        !isHistoryTravelReserved(state) &&
        state.segmentsMap[payload.segmentId]
      ) {
        if (
          state.activeTextEditHistory?.segmentId === payload.segmentId &&
          state.activeTextEditHistory.field === payload.field
        ) {
          return;
        }

        closeActiveTextEdit(state);
        const segment = state.segmentsMap[payload.segmentId];
        const pendingSourceSession =
          payload.field === 'src' ? state.pendingSourceTextEditHistories[payload.segmentId] : undefined;
        const before =
          pendingSourceSession?.before ||
          (payload.field === 'src' && segment.isEditingSrc
            ? state.segmentsFromServerMap[payload.segmentId]?.src || segment.src
            : segment[payload.field]);
        if (pendingSourceSession) {
          delete state.pendingSourceTextEditHistories[payload.segmentId];
        }
        state.activeTextEditHistory = {
          ...payload,
          before: cloneText(before),
          historyId: segment.historyId || segment.id,
        };
      }
    },

    clearEditorHistory: (state) => {
      state.activeHistoryTravel = null;
      state.activeTextEditHistory = null;
      state.pendingSourceTextEditHistories = {};
      state.historyRedoStack = [];
      state.historyUndoStack = [];
    },

    clearVoiceCloneErrorForSpeaker: (state, { payload }: PayloadAction<string>) => {
      Object.values(state.segmentsMap).forEach((segment) => {
        if (segment.speaker === payload && segment.isVoiceCloneError) {
          segment.isVoiceCloneError = false;
        }
      });
    },

    commitEditorHistoryAction: (state, { payload }: PayloadAction<{ operationId: string }>) => {
      if (
        !state.activeHistoryAction ||
        state.activeEditorOperationId !== payload.operationId ||
        state.activeHistoryAction.operationId !== payload.operationId
      ) {
        return;
      }

      const changes = Object.values(state.activeHistoryAction.changes)
        .map((change): TEditorStructuralHistoryChange => {
          const afterId = state.segmentsIds.find((id) => {
            const segment = state.segmentsMap[id];
            return segment && (segment.historyId || segment.id) === change.historyId;
          });
          const afterSegment = afterId ? state.segmentsMap[afterId] : undefined;

          return {
            ...change,
            after: afterSegment ? snapshotSegment(afterSegment) : undefined,
            afterIndex: afterId ? state.segmentsIds.indexOf(afterId) : undefined,
          };
        })
        .filter(
          ({ after, afterIndex, before, beforeIndex }) =>
            beforeIndex !== afterIndex || JSON.stringify(before) !== JSON.stringify(after),
        );

      pushHistoryEntry(state, { changes, kind: state.activeHistoryAction.kind });
      state.activeHistoryAction = null;
    },

    commitTextEditHistory: (state, { payload }: PayloadAction<{ segmentId: TServerSegment['id'] }>) => {
      const isActiveSession = state.activeTextEditHistory?.segmentId === payload.segmentId;
      const activeTextEdit = isActiveSession
        ? state.activeTextEditHistory
        : state.pendingSourceTextEditHistories[payload.segmentId];
      const segment = state.segmentsMap[payload.segmentId];
      const isUnconfirmedSourceSession = Boolean(activeTextEdit?.field === 'src' && segment?.isEditingSrc);

      if (activeTextEdit && segment && !isUnconfirmedSourceSession) {
        pushHistoryEntry(state, {
          after: cloneText(segment[activeTextEdit.field]),
          before: activeTextEdit.before,
          field: activeTextEdit.field,
          historyId: activeTextEdit.historyId,
          kind: 'text',
        });
      }
      if (isActiveSession) {
        state.activeTextEditHistory = null;
      } else if (!isUnconfirmedSourceSession) {
        delete state.pendingSourceTextEditHistories[payload.segmentId];
      }
    },

    completeEditorHistoryTravel: (
      state,
      {
        payload,
      }: PayloadAction<{
        authoritativeSegments: TServerSegment[];
        direction: 'redo' | 'undo';
        segments: TExtendedSegment[];
        deletedSegmentIds: TServerSegment['id'][];
      }>,
    ) => {
      const sourceStack = payload.direction === 'undo' ? state.historyUndoStack : state.historyRedoStack;
      const targetStack = payload.direction === 'undo' ? state.historyRedoStack : state.historyUndoStack;
      const entry = state.activeHistoryTravel?.entry || sourceStack.pop();

      if (entry && (!state.activeHistoryTravel || state.activeHistoryTravel.direction === payload.direction)) {
        targetStack.push(entry);
      }

      const previousSelectedHistoryId = state.selectedSegmentId
        ? state.segmentsMap[state.selectedSegmentId]?.historyId
        : undefined;
      const segmentsMap: Record<string, TExtendedSegment> = {};

      payload.segments.forEach((segment) => {
        const normalized = normalizeSegment(segment);
        segmentsMap[segment.id] = { ...normalized, historyId: segment.historyId || segment.id };
      });

      state.segmentsMap = segmentsMap;
      payload.deletedSegmentIds.forEach((segmentId) => {
        delete state.segmentsFromServerMap[segmentId];
      });
      payload.authoritativeSegments.forEach((segment) => {
        const normalized = normalizeSegment(segment);
        state.segmentsFromServerMap[normalized.id] = normalized;
      });
      state.segmentsIds = payload.segments.map(({ id }) => id);
      state.segmentsDataToSave = {};
      Object.keys(state.pendingSourceTextEditHistories).forEach((segmentId) => {
        if (!segmentsMap[segmentId]) {
          delete state.pendingSourceTextEditHistories[segmentId];
        }
      });
      state.selectedSegmentId =
        payload.segments.find(({ historyId }) => historyId === previousSelectedHistoryId)?.id || null;
      state.focusedSegmentId = null;
      state.activeHistoryTravel = null;
      state.historyApplying = false;
      state.transcriptVersionDirtyRevision += 1;
      recalculateSegmentOverlaps(state);
    },

    considerNewSegmentCreated: (
      state,
      {
        payload,
      }: PayloadAction<{
        temporarySegmentId: TServerSegment['id'];
      }>,
    ) => {
      state.newSegmentsData = state.newSegmentsData.filter(
        (segment) => segment.segment.id !== payload.temporarySegmentId,
      );
    },

    failEditorHistoryTravel: (state, { payload }: PayloadAction<{ reservationId: string }>) => {
      const reservedTravel = state.activeHistoryTravel;

      if (!reservedTravel || reservedTravel.reservationId !== payload.reservationId) {
        return;
      }

      const sourceStack = reservedTravel.direction === 'undo' ? state.historyUndoStack : state.historyRedoStack;
      sourceStack.push(reservedTravel.entry);
      state.activeHistoryTravel = null;
      state.historyApplying = false;
    },

    insertSegment: (state, { payload }: PayloadAction<TNewSegmentData>) => {
      if (state.historyApplying || isHistoryTravelReserved(state)) {
        return;
      }

      const parentSegmentIdx = state.segmentsIds.indexOf(payload.parentSegmentId);

      if (parentSegmentIdx !== -1) {
        const newSegmentIndex = payload.location === SegmentLocation.Top ? parentSegmentIdx : parentSegmentIdx + 1;

        state.segmentsMap[payload.segment.id] = {
          ...normalizeSegment(payload.segment),
          historyId: payload.segment.historyId || payload.segment.id,
        };
        state.segmentsIds.splice(newSegmentIndex, 0, payload.segment.id);
        trackInsertedSegment(state, payload.segment.id);
      }
    },

    invalidateEditorHistoryForRecovery: (state, { payload }: PayloadAction<{ reservationId: string }>) => {
      if (state.activeHistoryTravel?.reservationId !== payload.reservationId) {
        return;
      }

      state.activeHistoryAction = null;
      state.activeTextEditHistory = null;
      state.pendingSourceTextEditHistories = {};
      state.historyRedoStack = [];
      state.historyUndoStack = [];
    },

    // Drops the rendered dst audio for the given segments (preserving the rest of dst) so the
    // per-segment preview button falls back to idle after a voice/preset change invalidates it
    // server-side. Mirrors the backend nulling dst.audio in DynamoDB for the affected segments.
    invalidateSegmentsAudio: (state, { payload }: PayloadAction<string[]>) => {
      if (state.historyApplying || isHistoryTravelReserved(state)) {
        return;
      }

      payload.forEach((id) => {
        const segment = state.segmentsMap[id];

        if (segment?.dst) {
          segment.dst.audio = undefined;
        }

        const segmentFromServer = state.segmentsFromServerMap[id];

        if (segmentFromServer?.dst) {
          segmentFromServer.dst.audio = undefined;
        }
      });
    },

    markTranscriptVersionAutoSaveDirty: (state) => {
      state.transcriptVersionDirtyRevision += 1;
    },

    // Discards any pending version auto-save, e.g. after a rollback already snapshotted the
    // pre-rollback state server-side and the pending content no longer exists.
    markTranscriptVersionAutoSaveFullySynced: (state) => {
      state.transcriptVersionPersistedRevision = state.transcriptVersionDirtyRevision;
    },

    markTranscriptVersionAutoSaveSynced: (state, { payload }: PayloadAction<{ revision: number }>) => {
      // Guard against an older in-flight auto-save resolving after a newer one — the revision is
      // monotonic, so never regress the persisted revision.
      state.transcriptVersionPersistedRevision = Math.max(state.transcriptVersionPersistedRevision, payload.revision);
    },

    // Overlapped timestamps logic
    recalculateOverlappedTimestamps: (state) => {
      if (state.historyApplying || isHistoryTravelReserved(state)) {
        return;
      }

      state.segmentsIds.forEach((id) => {
        delete state.segmentsMap[id].overlappedTimestamps;
      });

      // segmentsIdsToCheck is for optimizing loop iterations
      const segmentsIdsToCheck = [...state.segmentsIds];

      state.segmentsIds.forEach((id) => {
        const overlappedTimestamps = getIntersectingTimestamps({
          segment: state.segmentsMap[id],
          segmentsIdsToCheck,
          segmentsMap: state.segmentsMap,
        });

        segmentsIdsToCheck.shift();

        Object.keys(overlappedTimestamps).forEach((segmentId) => {
          const segment = state.segmentsMap[segmentId];
          const isAlreadyExist = Boolean(segment?.overlappedTimestamps?.length);

          if (isAlreadyExist) {
            state.segmentsMap[segmentId].overlappedTimestamps = Array.from(
              new Set([
                ...(state.segmentsMap[segmentId].overlappedTimestamps || []),
                ...overlappedTimestamps[segmentId],
              ]),
            );
            return;
          }

          if (state.segmentsMap[segmentId]) {
            state.segmentsMap[segmentId].overlappedTimestamps = overlappedTimestamps[segmentId];
          }
        });
      });
    },

    reconcileFailedSegmentSave: (
      state,
      {
        payload,
      }: PayloadAction<{
        id: TExtendedSegment['id'];
        revertedData?: Partial<TExtendedSegment>;
      }>,
    ) => {
      const segment = state.segmentsMap[payload.id];

      if (!segment) {
        return;
      }

      state.segmentsMap[payload.id] = {
        ...segment,
        ...payload.revertedData,
        isSavingError: true,
      };
    },

    releaseEditorHistoryRecovery: (state, { payload }: PayloadAction<{ reservationId: string }>) => {
      if (state.activeHistoryTravel?.reservationId !== payload.reservationId) {
        return;
      }

      state.activeHistoryTravel = null;
      state.historyApplying = false;
    },

    releaseEditorOperation: (state, { payload }: PayloadAction<{ operationId: string }>) => {
      if (state.activeEditorOperationId !== payload.operationId) {
        return;
      }

      if (state.activeHistoryAction?.operationId === payload.operationId) {
        state.activeHistoryAction = null;
      }

      state.activeEditorOperationId = null;
    },

    reserveEditorHistoryTravel: (
      state,
      { payload }: PayloadAction<{ direction: 'redo' | 'undo'; reservationId: string }>,
    ) => {
      if (state.activeEditorOperationId || state.historyApplying || state.activeHistoryTravel) {
        return;
      }

      closeActiveTextEdit(state);
      const sourceStack = payload.direction === 'undo' ? state.historyUndoStack : state.historyRedoStack;
      const entry = sourceStack.pop();

      if (entry) {
        state.activeHistoryTravel = { ...payload, entry };
      }
    },

    startEditorHistoryTravel: (state, { payload }: PayloadAction<{ reservationId: string } | undefined>) => {
      if (state.activeEditorOperationId) {
        return;
      }

      if (payload && state.activeHistoryTravel?.reservationId !== payload.reservationId) {
        return;
      }

      if (!payload) {
        closeActiveTextEdit(state);
      }

      state.historyApplying = true;
    },

    tryAcquireEditorOperation: (
      state,
      { payload }: PayloadAction<{ historyKind?: TEditorHistoryKind; operationId: string }>,
    ) => {
      if (state.activeEditorOperationId || state.historyApplying || isHistoryTravelReserved(state)) {
        return;
      }

      if (state.activeTextEditHistory) {
        closeActiveTextEdit(state);
      }

      state.activeEditorOperationId = payload.operationId;

      if (payload.historyKind) {
        state.activeHistoryAction = {
          changes: {},
          kind: payload.historyKind as TEditorStructuralHistoryKind,
          operationId: payload.operationId,
        };
      }
    },

    updSegment: (state, { payload }: PayloadAction<{ id: TExtendedSegment['id'] } & Partial<TExtendedSegment>>) => {
      if (state.historyApplying || isHistoryTravelReserved(state)) {
        return;
      }

      if (state.segmentsMap[payload.id]) {
        trackSegmentBeforeMutation(state, payload.id);
        state.segmentsMap[payload.id] = { ...state.segmentsMap[payload.id], ...payload };
      }
    },

    updSegmentAfterProcessing: (state, { payload }: PayloadAction<TServerSegment[]>) => {
      if (state.historyApplying || isHistoryTravelReserved(state)) {
        return;
      }

      payload.forEach((updatedSegment) => {
        const segmentFromServer = state.segmentsFromServerMap[updatedSegment.id];
        const isFinalStatus = updatedSegment.status !== SegmentStatus.Processing;

        if (segmentFromServer && state.segmentsMap[updatedSegment.id]) {
          state.segmentsMap[updatedSegment.id].processing_step = updatedSegment.processing_step;
          state.segmentsMap[updatedSegment.id].processing_started_at = updatedSegment.processing_started_at;

          if (isFinalStatus) {
            const normalized = normalizeSegment(updatedSegment);

            state.segmentsFromServerMap[updatedSegment.id] = normalized;

            state.segmentsMap[updatedSegment.id].status = normalized.status;
            state.segmentsMap[updatedSegment.id].src = normalized.src;
            state.segmentsMap[updatedSegment.id].dst = normalized.dst;
            // Re-translation (e.g. after a merge) regenerates the alternatives, so sync them into
            // segmentsMap — the only map useTranslationVariants reads. Without this the picker keeps
            // showing the pre-processing variants. Guarded by isFinalStatus like src/dst above so an
            // in-flight payload never clobbers a concurrent user edit.
            state.segmentsMap[updatedSegment.id].translation_variants = normalized.translation_variants;
            state.segmentsMap[updatedSegment.id].selected_translation_variant = normalized.selected_translation_variant;

            if (updatedSegment.status === SegmentStatus.Error) {
              state.segmentsMap[updatedSegment.id].isSavingError = true;
            }
          }
        }
      });
    },

    updSegmentFromServer: (state, { payload }: PayloadAction<TServerSegment>) => {
      if (state.historyApplying) {
        return;
      }

      state.segmentsFromServerMap[payload.id] = payload;
    },

    updSegmentIsSavingError: (
      state,
      { payload }: PayloadAction<{ id: TExtendedSegment['id'] } & Partial<TExtendedSegment>>,
    ) => {
      if (state.historyApplying) {
        return;
      }

      const segment = state.segmentsMap[payload.id];

      if (segment && segment.isSavingError !== payload.isSavingError) {
        segment.isSavingError = payload.isSavingError;
      }
    },

    // New segments logic
    addNewSegment: (
      state,
      {
        payload,
      }: PayloadAction<{ location: TSegmentLocation; parentSegment: TServerSegment; segment: TServerSegment }>,
    ) => {
      if (state.historyApplying || isHistoryTravelReserved(state)) {
        return;
      }

      state.newSegmentsData.push({
        location: payload.location,
        parentSegmentId: payload.parentSegment.id,
        segment: payload.segment,
      });
    },

    cancelTextEditHistory: (state) => {
      if (
        state.activeTextEditHistory?.field === 'src' &&
        !state.segmentsMap[state.activeTextEditHistory.segmentId]?.isEditingSrc
      ) {
        state.activeTextEditHistory = null;
      }

      Object.keys(state.pendingSourceTextEditHistories).forEach((segmentId) => {
        if (!state.segmentsMap[segmentId]?.isEditingSrc) {
          delete state.pendingSourceTextEditHistories[segmentId];
        }
      });
    },

    removeMatchingSegmentDataToSave: (
      state,
      { payload }: PayloadAction<{ changedData: TAutoSavingSegmentData; segmentId: TServerSegment['id'] }>,
    ) => {
      const queuedData = state.segmentsDataToSave[payload.segmentId];

      if (!queuedData) {
        return;
      }

      (Object.keys(payload.changedData) as (keyof TAutoSavingSegmentData)[]).forEach((key) => {
        if (JSON.stringify(queuedData[key]) === JSON.stringify(payload.changedData[key])) {
          delete queuedData[key];
        }
      });

      if (Object.keys(queuedData).length === 0) {
        delete state.segmentsDataToSave[payload.segmentId];
      }
    },

    removeSegment: (
      state,
      { payload }: PayloadAction<{ id: TServerSegment['id']; leaveInSegmentsFromServer?: boolean }>,
    ) => {
      if (state.historyApplying || isHistoryTravelReserved(state)) {
        return;
      }

      trackSegmentBeforeMutation(state, payload.id);
      state.segmentsIds = state.segmentsIds.filter((id) => id !== payload.id);

      delete state.segmentsMap[payload.id];
      delete state.segmentsDataToSave[payload.id];
      delete state.pendingSourceTextEditHistories[payload.id];

      if (!payload.leaveInSegmentsFromServer) {
        delete state.segmentsFromServerMap[payload.id];
      }
    },

    removeSegmentDataToSave: (
      state,
      { payload }: PayloadAction<{ changedDataKey?: keyof TAutoSavingSegmentData; segmentId: TServerSegment['id'] }>,
    ) => {
      if (!state.segmentsDataToSave[payload.segmentId]) {
        return;
      }

      // We can remove a particular field in the segmentsDataToSave object:
      if (payload.changedDataKey) {
        delete state.segmentsDataToSave[payload.segmentId][payload.changedDataKey];

        const isEmptyObject = Object.keys(state.segmentsDataToSave[payload.segmentId]).length === 0;

        if (isEmptyObject) {
          delete state.segmentsDataToSave[payload.segmentId];
        }
      } else {
        // Or remove the whole segmentsDataToSave object
        delete state.segmentsDataToSave[payload.segmentId];
      }
    },

    // State resetting
    resetEditorState: (state) => createInitialState(state.transcriptReplacedEpoch + 1),

    setAllSegments: (state, { payload: segments }: PayloadAction<TServerSegment[]>) => {
      const segmentsMap: Record<string, TServerSegment> = {};
      const editableSegmentsMap: Record<string, TExtendedSegment> = {};

      for (const segment of segments) {
        const normalizedSegment = normalizeSegment(segment);
        segmentsMap[normalizedSegment.id] = normalizedSegment;
        editableSegmentsMap[normalizedSegment.id] = {
          ...normalizedSegment,
          historyId: normalizedSegment.id,
        };
      }

      state.segmentsFromServerMap = segmentsMap;
      state.segmentsMap = editableSegmentsMap;
      state.segmentsIds = segments.map(({ id }) => id);
      // A wholesale server refresh may represent a different project or recovery after a partially
      // applied operation. No command recorded against the previous topology is safe to replay.
      state.activeEditorOperationId = null;
      state.activeHistoryAction = null;
      state.activeHistoryTravel = null;
      state.activeTextEditHistory = null;
      state.newSegmentsData = [];
      state.pendingSourceTextEditHistories = {};
      state.historyApplying = false;
      state.historyRedoStack = [];
      state.historyUndoStack = [];
      state.segmentsDataToSave = {};
      state.transcriptReplacedEpoch += 1;
    },

    // Focused segment logic
    setFocusedSegmentId: (state, { payload }: PayloadAction<TEditorStore['focusedSegmentId']>) => {
      state.focusedSegmentId = payload;
    },

    setIsAutoSaving: (state, { payload }: PayloadAction<boolean>) => {
      state.isAutoSaving = payload;
    },

    // Voice suggest logic
    setIsVoiceSuggesting: (state, { payload }: PayloadAction<boolean>) => {
      state.isVoiceSuggesting = payload;
    },

    // Auto save logic
    setSegmentDataToSave: (
      state,
      { payload }: PayloadAction<{ id: TServerSegment['id'] } & TAutoSavingSegmentData>,
    ) => {
      if (state.historyApplying || isHistoryTravelReserved(state)) {
        return;
      }

      const { id, ...segmentData } = payload;

      state.segmentsDataToSave[id] = state.segmentsDataToSave[id]
        ? { ...state.segmentsDataToSave[id], ...segmentData }
        : segmentData;
    },

    setSelectedSegmentId: (state, { payload }: PayloadAction<TEditorStore['selectedSegmentId']>) => {
      state.selectedSegmentId = payload;
    },

    setServerSegments: (state, { payload }: PayloadAction<TServerSegment[]>) => {
      if (state.historyApplying) {
        return;
      }

      const segmentsMap: Record<string, TServerSegment> = {};

      for (const segment of payload) {
        const normalizedSegment = normalizeSegment(segment);
        segmentsMap[normalizedSegment.id] = normalizedSegment;
      }

      state.segmentsFromServerMap = segmentsMap;
    },

    setStatus: (state, { payload }: PayloadAction<{ id: TServerSegment['id']; status: TServerSegment['status'] }>) => {
      if (!state.historyApplying && !isHistoryTravelReserved(state) && state.segmentsMap[payload.id]) {
        state.segmentsMap[payload.id].status = payload.status;
      }
    },
  },
});

export const editorReducer = (state: TEditorStore | undefined, action: UnknownAction): TEditorStore =>
  editorSlice.reducer(state, action);

export const {
  applyHistoryFlushResponse,
  beginTextEditHistory,
  clearEditorHistory,
  clearVoiceCloneErrorForSpeaker,
  commitEditorHistoryAction,
  commitTextEditHistory,
  completeEditorHistoryTravel,
  considerNewSegmentCreated,
  failEditorHistoryTravel,
  insertSegment,
  invalidateEditorHistoryForRecovery,
  invalidateSegmentsAudio,
  markTranscriptVersionAutoSaveDirty,
  markTranscriptVersionAutoSaveFullySynced,
  markTranscriptVersionAutoSaveSynced,
  recalculateOverlappedTimestamps,
  reconcileFailedSegmentSave,
  releaseEditorHistoryRecovery,
  releaseEditorOperation,
  reserveEditorHistoryTravel,
  startEditorHistoryTravel,
  tryAcquireEditorOperation,
  updSegment,
  updSegmentAfterProcessing,
  updSegmentFromServer,
  updSegmentIsSavingError,
  addNewSegment,
  cancelTextEditHistory,
  removeMatchingSegmentDataToSave,
  removeSegment,
  removeSegmentDataToSave,
  resetEditorState,
  setAllSegments,
  setFocusedSegmentId,
  setIsAutoSaving,
  setIsVoiceSuggesting,
  setSegmentDataToSave,
  setSelectedSegmentId,
  setServerSegments,
  setStatus,
} = editorSlice.actions;
